import React, { useState } from 'react';
import useManageOrder from '../order/useManageOrder';
import DualShipDateDropdown from '../../sharedComponents/DualShipDateDropdown';
import OrderSummaryTable from '../../sharedComponents/OrderSummaryTable';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Modal from 'react-modal';
import OrderDetail from '../customerProfile/OrderDetail';

const customStylesOrderDetail = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000
  },
  content: {
    top: '5%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    width: '1100px',
    maxWidth: '95vw',
    maxHeight: '90vh',
    height: 'auto',
    transform: 'translateX(-50%)',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
};

const PAGE_SIZE_OPTIONS = [100, 500];

function OrderSummaryAdmin(props) {

  const { orders, isLoading, error, getOrderSummary } = useManageOrder()
  const [formData, setFormData] = useState({
    searchtext: '',
    shipDate: 'all',
    shipDateIds: undefined,
    multiShipType: undefined,
  })
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderDetailOpen, setIsOrderDetailOpen] = useState(false);
  const shopName = useSelector((state) => state.shop.name);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const buildOrderSummaryPayload = (page = 1, limit = pageSize) => {
    const shop = shopName === 'tama' ? 'tama' : 'smile';
    return {
      searchtext: formData.searchtext,
      shop,
      shipDate: formData.shipDate,
      ...(formData.shipDate === 'multi'
        ? {
            shipDateIds: formData.shipDateIds,
            multiShipType: formData.multiShipType,
          }
        : {}),
      limit,
      offset: (page - 1) * limit,
    };
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (formData.searchtext === '') {
      toast.error("กรุณาใส่เบอร์โทรศัพท์")
    } else if (
      formData.shipDate === 'multi' &&
      (!formData.shipDateIds || formData.shipDateIds.length === 0)
    ) {
      toast.error('กรุณาเลือกอย่างน้อยหนึ่งรอบเมื่อใช้โหมดเลือกหลายรอบ')
    } else {
      try {
        setCurrentPage(1);
        const result = await getOrderSummary(buildOrderSummaryPayload(1, pageSize));
        if (result?.success) {
          setTotalCount(result.totalCount || 0);
        }
      } catch (error) {}
    }
  };

  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > totalPages || isLoading) return;
    setCurrentPage(newPage);
    try {
      await getOrderSummary(buildOrderSummaryPayload(newPage, pageSize));
    } catch (error) {}
  };

  const handlePageSizeChange = async (newSize) => {
    const size = parseInt(newSize, 10);
    setPageSize(size);
    setCurrentPage(1);
    try {
      const result = await getOrderSummary(buildOrderSummaryPayload(1, size));
      if (result?.success) {
        setTotalCount(result.totalCount || 0);
      }
    } catch (error) {}
  };

  const handleDataChange = (e) => {
    if (e && e.target) {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });
    } else if (e && typeof e === 'object' && e.shipDate === 'multi') {
      setFormData({
        ...formData,
        shipDate: 'multi',
        shipDateIds: e.shipDateIds,
        multiShipType: e.multiShipType,
      });
    } else {
      setFormData({
        ...formData,
        shipDate: e,
        shipDateIds: undefined,
        multiShipType: undefined,
      });
    }
  };

  const handleOpenOrderDetail = (order) => {
    setSelectedOrder(order);
    setIsOrderDetailOpen(true);
  };

  const handleCloseOrderDetail = () => {
    setIsOrderDetailOpen(false);
    setSelectedOrder(null);
  };

  const handleSuccessUpdate = () => {
    // Refresh orders after update
    if(formData.searchtext) {
      getOrderSummary(buildOrderSummaryPayload());
    }
  };

  return (
    <div className="xl:w-4/5 mx-auto flex flex-col items-center justify-center mt-1 min-h-screen">
      <img className="h-64 w-64" src="https://image.makewebeasy.net/makeweb/m_1920x0/mawDVKUgN/DefaultData/Logo__1_.png?v=202305101549" alt="Logo" />
      <div className="w-full text-center">
        <form id="search-form" onSubmit={handleFormSubmit} className="mb-2 flex flex-col items-center">
          <div>
            <label htmlFor="searchtext">กรุณากรอกเบอร์โทรศัพท์ที่ต้องการค้นหา</label>
          </div>
          <div>
            <input type="text" className="pl-2 border border-gray-500 rounded" id="searchtext" name="searchtext" onChange={handleDataChange}/>
            <button type="submit" className="btn btn-blue mb-1 ml-2">ค้นหา</button>
            {isLoading && <CircularProgress />}
          </div>
          <div className='mb-4'>
            <DualShipDateDropdown
              handleDataChange={handleDataChange}
              allowMultiShipDate={true}
            />
          </div>
        </form>
        {error && <p className='text-red-600'>{error}</p>}
        <OrderSummaryTable orders={orders} isAdmin={true} onOpenOrderDetail={handleOpenOrderDetail}/>

        {/* Pagination controls */}
        {totalCount > 0 && (
          <div className="mt-4 flex flex-col items-center gap-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || isLoading}
                className="px-2 py-1 border rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                «
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className="px-3 py-1 border rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                ‹
              </button>
              <span className="text-sm text-gray-700">
                หน้า <strong>{currentPage}</strong> / <strong>{totalPages}</strong>
                &nbsp;({totalCount.toLocaleString()} รายการ)
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className="px-3 py-1 border rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                ›
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                className="px-2 py-1 border rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                »
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>รายการต่อหน้า:</span>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <button
                  key={size}
                  onClick={() => handlePageSizeChange(size)}
                  disabled={isLoading}
                  className={`px-3 py-1 border rounded disabled:cursor-not-allowed ${
                    pageSize === size
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <Modal
        isOpen={isOrderDetailOpen}
        onRequestClose={handleCloseOrderDetail}
        style={customStylesOrderDetail}
        ariaHideApp={false}
      >
        {selectedOrder && (
          <OrderDetail 
            order={selectedOrder} 
            handleSuccessUpdate={handleSuccessUpdate}
          />
        )}
      </Modal>
    </div>
  );  
}

export default OrderSummaryAdmin;